import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TextFieldModule } from '@angular/cdk/text-field';
import { VisitorChatService, VisitorChatMessage } from '../../services/visitor-chat.service';
import { Subscription, Observable } from 'rxjs';

@Component({
  selector: 'app-visitor-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    TextFieldModule
  ],
  template: `
    <div class="chat-container" [class.open]="isOpen">
      <button mat-fab class="chat-button" (click)="toggleChat()">
        <mat-icon>{{ isOpen ? 'close' : 'chat' }}</mat-icon>
      </button>

      <div class="chat-window" *ngIf="isOpen">
        <div class="chat-header">
          <h3>Chat mit uns</h3>
        </div>

        <div class="chat-content" *ngIf="currentStep === 'chat'">
          <div class="messages" #messagesContainer>
            <div *ngFor="let message of messages$ | async" 
                 class="message" 
                 [class.visitor]="message.senderId === 'visitor'"
                 [class.admin]="message.senderId === 'admin'"
                 [class.system]="message.senderId === 'system'">
              <div class="message-content" [innerText]="message.text"></div>
              <div class="message-time">{{ message.timestamp | date:'shortTime' }}</div>
            </div>

            <!-- Resolved Chat Message -->
            <div class="resolved-message" *ngIf="status === 'resolved'">
              <div class="resolved-content">
                <mat-icon>check_circle</mat-icon>
                <h3>Chat beendet</h3>
                <p>Vielen Dank für Ihre Anfrage. Dieser Chat wurde als gelöst markiert.</p>
                <button mat-flat-button color="accent" (click)="startNewChat()">
                  Neuen Chat starten
                </button>
              </div>
            </div>
          </div>

          <div class="message-input" *ngIf="status === 'active'">
            <mat-form-field appearance="outline" class="full-width">
              <textarea matInput 
                       [(ngModel)]="newMessage" 
                       placeholder="Nachricht eingeben..."
                       cdkTextareaAutosize
                       #autosize="cdkTextareaAutosize"
                       [cdkAutosizeMinRows]="1"
                       [cdkAutosizeMaxRows]="4"
                       (keydown)="onKeyDown($event)"></textarea>
            </mat-form-field>
            <button mat-icon-button (click)="sendMessage()" [disabled]="!newMessage.trim()">
              <mat-icon>send</mat-icon>
            </button>
          </div>
        </div>

        <div class="onboarding" *ngIf="currentStep === 'name'">
          <h4>Hallo! Wie ist dein Name?</h4>
          <mat-form-field appearance="outline" class="full-width">
            <input matInput [(ngModel)]="visitorName" placeholder="Name eingeben">
          </mat-form-field>
          <button mat-raised-button 
                  [disabled]="!visitorName.trim()" 
                  (click)="nextStep()">
            Weiter
          </button>
        </div>

        <div class="onboarding" *ngIf="currentStep === 'email'">
          <h4>Bitte geben Sie Ihre E-Mail-Adresse ein</h4>
          <mat-form-field appearance="outline" class="full-width">
            <input matInput 
                   type="email" 
                   [(ngModel)]="visitorEmail" 
                   placeholder="E-Mail eingeben"
                   pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$">
          </mat-form-field>
          <button mat-raised-button 
                  [disabled]="!isValidEmail(visitorEmail)" 
                  (click)="nextStep()">
            Chat starten
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      --chat-primary: #FFD700;
      --chat-bg: #1a1a1a;
      --chat-text: #ffffff;
      --chat-secondary-text: #999999;
      --chat-border: #333333;
      --chat-message-bg: #2a2a2a;
      --chat-visitor-message-bg: #FFD700;
      --chat-visitor-message-text: #000000;
    }

    .chat-container {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 1000;
    }

    .chat-button {
      position: absolute;
      bottom: 0;
      right: 0;
      background-color: var(--chat-primary) !important;
      color: var(--chat-visitor-message-text) !important;
      box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
    }

    .chat-window {
      position: absolute;
      bottom: 80px;
      right: 0;
      width: 320px;
      height: 480px;
      background: var(--chat-bg);
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid var(--chat-border);
      color: var(--chat-text);
    }

    .chat-header {
      padding: 16px;
      background: var(--chat-primary);
      color: var(--chat-visitor-message-text);
      h3 {
        margin: 0;
        font-weight: 500;
      }
    }

    .chat-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .messages {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      &::-webkit-scrollbar {
        width: 6px;
      }
      &::-webkit-scrollbar-track {
        background: var(--chat-bg);
      }
      &::-webkit-scrollbar-thumb {
        background: var(--chat-border);
        border-radius: 3px;
      }
    }

    .message {
      margin-bottom: 12px;
      max-width: 85%;

      &.visitor {
        margin-left: auto;
        .message-content {
          background: var(--chat-visitor-message-bg);
          color: var(--chat-visitor-message-text);
        }
      }

      &.admin {
        margin-right: auto;
        .message-content {
          background: var(--chat-message-bg);
          color: var(--chat-text);
        }
      }

      &.system {
        align-self: center;
        max-width: 90%;
        margin: 20px auto;
        
        .message-content {
          background: var(--chat-message-bg);
          border: 1px solid var(--chat-border);
          color: var(--chat-secondary-text);
          font-style: italic;
          text-align: center;
        }
      }
    }

    .message-content {
      padding: 10px 14px;
      border-radius: 16px;
      word-break: break-word;
      font-size: 14px;
      white-space: pre-wrap;
      line-height: 1.4;
    }

    .message-time {
      font-size: 11px;
      color: var(--chat-secondary-text);
      margin-top: 4px;
      text-align: right;
    }

    .message-input {
      padding: 16px;
      background: var(--chat-bg);
      border-top: 1px solid var(--chat-border);
      display: flex;
      gap: 8px;
      align-items: flex-start;

      .mat-mdc-form-field {
        flex: 1;
      }

      textarea {
        resize: none;
        line-height: 1.5;
        padding: 8px 0;
      }

      button {
        margin-top: 8px;
      }
    }

    .onboarding {
      padding: 24px;
      text-align: center;
      background: var(--chat-bg);

      h4 {
        margin-bottom: 20px;
        color: var(--chat-text);
        font-weight: 400;
      }

      button {
        background-color: var(--chat-primary);
        color: var(--chat-visitor-message-text);
        margin-top: 16px;

        &:disabled {
          background-color: var(--chat-message-bg);
          color: var(--chat-secondary-text);
        }
      }

      ::ng-deep {
        .mat-mdc-form-field {
          .mat-mdc-text-field-wrapper {
            background: var(--chat-message-bg);
          }
          .mat-mdc-form-field-flex {
            background: var(--chat-message-bg);
          }
          .mdc-text-field--outlined:not(.mdc-text-field--disabled) .mdc-notched-outline__leading,
          .mdc-text-field--outlined:not(.mdc-text-field--disabled) .mdc-notched-outline__notch,
          .mdc-text-field--outlined:not(.mdc-text-field--disabled) .mdc-notched-outline__trailing {
            border-color: var(--chat-border);
          }
          .mat-mdc-form-field-infix {
            color: var(--chat-text);
          }
        }
      }
    }

    .full-width {
      width: 100%;
    }

    .resolved-message {
      margin: 20px;
      padding: 20px;
      background: var(--chat-message-bg);
      border: 1px solid var(--chat-border);
      border-radius: 12px;
      text-align: center;

      .resolved-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;

        mat-icon {
          font-size: 32px;
          width: 32px;
          height: 32px;
          color: var(--chat-primary);
        }

        h3 {
          margin: 0;
          color: var(--chat-text);
          font-size: 18px;
        }

        p {
          margin: 0;
          color: var(--chat-secondary-text);
          font-size: 14px;
        }

        button {
          margin-top: 12px;
          background: var(--chat-primary);
          color: var(--chat-visitor-message-text);

          &:hover {
            background: #e6c200;
          }
        }
      }
    }
  `]
})
export class VisitorChatComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  isOpen = false;
  chatId = '';
  currentStep: 'name' | 'email' | 'chat' = 'name';
  visitorName = '';
  visitorEmail = '';
  newMessage = '';
  status: 'active' | 'resolved' = 'active';
  messages$: Observable<VisitorChatMessage[]>;
  messages: VisitorChatMessage[] = [];
  private subscriptions: Subscription[] = [];

  constructor(private visitorChatService: VisitorChatService) {
    this.messages$ = new Observable<VisitorChatMessage[]>();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.visitorChatService.chatOpen$.subscribe(
        isOpen => this.isOpen = isOpen
      )
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    this.visitorChatService.toggleChat();
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  nextStep() {
    if (this.currentStep === 'name' && this.visitorName.trim()) {
      this.currentStep = 'email';
    } else if (this.currentStep === 'email' && this.isValidEmail(this.visitorEmail)) {
      this.initializeChat();
    }
  }

  async initializeChat() {
    try {
      this.chatId = await this.visitorChatService.initializeChat(this.visitorName, this.visitorEmail);
      this.currentStep = 'chat';
      this.status = 'active';
      this.messages$ = this.visitorChatService.getMessages(this.chatId);
      
      // Subscribe to chat status changes
      this.subscriptions.push(
        this.visitorChatService.getChatStatus(this.chatId).subscribe(
          (chatStatus: 'active' | 'resolved') => {
            this.status = chatStatus;
            // Scroll to bottom when status changes to show resolved message
            if (chatStatus === 'resolved') {
              setTimeout(() => this.scrollToBottom(), 100);
            }
          }
        )
      );
    } catch (error) {
      console.error('Failed to initialize chat:', error);
    }
  }

  async sendMessage() {
    if (!this.newMessage.trim() || !this.chatId) return;

    try {
      await this.visitorChatService.sendMessage(this.chatId, this.newMessage.trim());
      this.newMessage = '';
      setTimeout(() => this.scrollToBottom(), 100);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }

  async startNewChat() {
    try {
      // If there's an existing chat, send a system message before resetting
      if (this.chatId) {
        await this.visitorChatService.sendMessage(
          this.chatId,
          'Der Chat wurde als gelöst markiert. Sie können einen neuen Chat starten, wenn Sie weitere Fragen haben.',
          'system'
        );
      }

      // Reset all chat state
      this.chatId = '';
      this.currentStep = 'name';
      this.visitorName = '';
      this.visitorEmail = '';
      this.newMessage = '';
      this.status = 'active';
      
      // Keep chat window open and show onboarding
      this.isOpen = true;
    } catch (error) {
      console.error('Failed to start new chat:', error);
    }
  }

  private scrollToBottom(): void {
    try {
      const element = this.messagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    } catch (err) {}
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        // Allow new line when Shift+Enter is pressed
        return;
      } else {
        // Send message when only Enter is pressed
        event.preventDefault();
        this.sendMessage();
      }
    }
  }
} 